import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { generateImageAlt } from "@/lib/image-alt";
import { ImageAltAuditPanel } from "@/components/admin/ImageAltAuditPanel";
import { HomeArticleBoxesPanel } from "@/components/admin/HomeArticleBoxes";
import { AnalyticsDashboard } from "@/components/admin/AnalyticsDashboard";
import { ArticlePreview } from "@/components/admin/ArticlePreview";
import { toast } from "sonner";
import { makeSeoDescription } from "@/lib/seo-description";
import {
  adminLogin,
  adminLogout,
  adminStatus,
  aiAssistant,
  publishArticle,
  removeArticle,
  setGeminiKey,
} from "@/lib/admin.functions";
import {
  adminCreateBox,
  adminDeleteBox,
  adminDeleteBoxArticle,
  adminGenerateBoxArticle,
  adminHomeBoxes,
  adminSaveBoxArticle,
} from "@/lib/home-box.functions";
import {
  adminDeleteStory,
  adminListStories,
  adminSetStoryStatus,
} from "@/lib/community.functions";


export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin Dashboard | LifeWestWay" },
      { name: "description", content: makeSeoDescription("Private admin dashboard for LifeWestWay editors.") },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Admin Dashboard | LifeWestWay" },
      { property: "og:description", content: makeSeoDescription("Private admin dashboard for LifeWestWay editors.") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminPage,
});

const COUNTRIES = ["usa", "uk", "canada", "australia", "italy", "portugal"] as const;
const CATEGORIES = ["Immigration", "Jobs", "Finance", "Tax", "Travel"] as const;

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary";
const labelClass = "mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground";
const btnPrimary =
  "inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent";

interface FormState {
  id?: string;
  title: string;
  slug: string;
  country: string;
  category: string;
  excerpt: string;
  heroImageUrl: string;
  heroImageAlt: string;
  heroImageAltManual?: boolean;
  bodyHtml: string;
  published: boolean;
}

const EMPTY_FORM: FormState = {
  title: "",
  slug: "",
  country: "usa",
  category: "Immigration",
  excerpt: "",
  heroImageUrl: "",
  heroImageAlt: "",
  heroImageAltManual: false,
  bodyHtml: "",
  published: true,
};

function AdminPage() {
  const status = useQuery({
    queryKey: ["admin-status"],
    queryFn: useServerFn(adminStatus),
    retry: false,
  });

  if (status.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
        Loading admin…
      </div>
    );
  }

  if (!status.data?.authed) return <LoginCard />;

  return (
    <Dashboard
      articles={status.data.articles ?? []}
      categories={status.data.categories ?? []}
      hasGeminiKey={status.data.hasGeminiKey ?? false}
    />
  );
}


function LoginCard() {
  const queryClient = useQueryClient();
  const login = useServerFn(adminLogin);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await login({ data: { email, password } });
      if (!res.ok) {
        setError(res.error ?? "Login failed.");
        return;
      }
      localStorage.setItem("lww_admin", "1");
      await queryClient.invalidateQueries({ queryKey: ["admin-status"] });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-[75vh] items-center justify-center px-4 py-16">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-lg"
      >
        <h1 className="font-display text-2xl font-bold text-foreground">Admin Login</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Restricted area. Authorised editors only.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className={labelClass} htmlFor="admin-email">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="admin-password">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </div>

        {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}

        <button type="submit" className={`${btnPrimary} mt-6 w-full`} disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

interface ArticleRow {
  id: string;
  title: string;
  slug: string;
  country: string;
  category: string;
  published: boolean;
  published_at: string;
}

function Dashboard({
  articles,
  categories,
  hasGeminiKey,
}: {
  articles: ArticleRow[];
  categories: string[];
  hasGeminiKey: boolean;
}) {
  const queryClient = useQueryClient();
  const logout = useServerFn(adminLogout);
  const [tab, setTab] = useState<"overview" | "analytics" | "editor" | "ai-post-box" | "home-boxes" | "stories" | "alt-audit" | "settings">("overview");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-status"] });

  const categorySet = new Set([...categories, ...articles.map((a) => a.category)]);
  const countries = new Set(articles.map((a) => a.country));


  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground">LifeWestWay content control centre</p>
        </div>
        <button
          className={btnGhost}
          onClick={async () => {
            await logout({});
            localStorage.removeItem("lww_admin");
            await refresh();
          }}
        >
          Sign out
        </button>
      </header>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Total posts" value={articles.length} />
        <StatCard label="Categories" value={categorySet.size} />
        <StatCard label="Countries covered" value={countries.size} />
      </div>

      <nav className="mt-8 flex flex-wrap gap-2 border-b border-border pb-2">
        {(
          [
            ["overview", "Manage posts"],
            ["analytics", "Live Analytics"],
            ["editor", "New / edit post"],
            ["ai-post-box", "AI POST BOX"],
            ["home-boxes", "হোম আর্টিকেল বক্স"],
            ["stories", "Community stories"],
            ["alt-audit", "Image alt audit"],
            ["settings", "Gemini API key"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              tab === key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === "overview" ? (
          <ManagePosts
            articles={articles}
            onEdit={(a) => {
              setForm({
                id: a.id,
                title: a.title,
                slug: a.slug,
                country: a.country,
                category: a.category,
                excerpt: "",
                heroImageUrl: "",
                heroImageAlt: "",
                heroImageAltManual: false,
                bodyHtml: "",
                published: a.published,
              });
              setTab("editor");
            }}
            onChanged={refresh}
          />
        ) : null}

        {tab === "analytics" ? <AnalyticsDashboard /> : null}
        {tab === "editor" ? (
          <PostEditor
            form={form}
            setForm={setForm}
            categories={[...categorySet].filter(Boolean).sort()}
            hasGeminiKey={hasGeminiKey}
            onChanged={refresh}
            onPublished={async () => {
              await refresh();
              setTab("overview");
              setForm(EMPTY_FORM);
            }}
          />
        ) : null}


        {tab === "ai-post-box" ? (
          <AiPostBox
            categories={[...categorySet].filter(Boolean).sort()}
            onChanged={refresh}
          />
        ) : null}
        {tab === "home-boxes" ? <HomeArticleBoxesPanel /> : null}

        {tab === "stories" ? <CommunityStoriesPanel /> : null}

        {tab === "alt-audit" ? <ImageAltAuditPanel /> : null}

        {tab === "settings" ? (
          <ApiKeyPanel hasGeminiKey={hasGeminiKey} onSaved={refresh} />
        ) : null}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function ManagePosts({
  articles,
  onEdit,
  onChanged,
}: {
  articles: ArticleRow[];
  onEdit: (a: ArticleRow) => void;
  onChanged: () => void;
}) {
  const del = useServerFn(removeArticle);

  if (articles.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No posts yet. Create your first one from the “New / edit post” tab.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead className="bg-accent/50 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Title</th>
            <th className="px-4 py-3">Country</th>
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {articles.map((a) => (
            <tr key={a.id} className="border-t border-border">
              <td className="px-4 py-3 font-medium text-foreground">
                <a title={`Preview published post: ${a.title}`} className="hover:underline" href={`/post/${a.slug}`} target="_blank" rel="noreferrer">
                  {a.title}
                </a>
              </td>
              <td className="px-4 py-3 uppercase text-muted-foreground">{a.country}</td>
              <td className="px-4 py-3 text-muted-foreground">{a.category}</td>
              <td className="px-4 py-3 text-muted-foreground">
                {a.published ? "Live" : "Draft"}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <button className={btnGhost} onClick={() => onEdit(a)}>
                    Edit
                  </button>
                  <button
                    className={btnGhost}
                    onClick={async () => {
                      if (!confirm(`Delete “${a.title}”?`)) return;
                      await del({ data: { id: a.id } });
                      toast.success("Post deleted");
                      onChanged();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  link?: string;
}

function PostEditor({
  form,
  setForm,
  categories,
  hasGeminiKey,
  onChanged,
  onPublished,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  categories: string[];
  hasGeminiKey: boolean;
  onChanged: () => void;
  onPublished: () => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      text:
        "Hi! Paste a finished article or raw notes and tell me where it belongs — e.g. “Put this article inside the Visa menu for USA”, or “Create a new menu called Digital Nomad and put this post there”. I'll format it, file it under the right menu and publish it for you.",
    },
  ]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const runAi = useServerFn(aiAssistant);
  const publish = useServerFn(publishArticle);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const aiMutation = useMutation({
    mutationFn: async (text: string) => runAi({ data: { prompt: text, autoPublish } }),
    onSuccess: (res) => {
      const draft = res.draft;
      setForm((prev) => ({
        ...prev,
        id: res.published ? undefined : prev.id,
        title: draft.title,
        slug: draft.slug,
        country: draft.country,
        category: draft.category,
        excerpt: draft.excerpt,
        bodyHtml: draft.bodyHtml,
      }));
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: res.message,
          link: res.published ? `/post/${res.article.slug}` : undefined,
        },
      ]);
      onChanged();
      if (res.published) toast.success("Published live");
    },
    onError: (err: Error) =>
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${err.message}` }]),
  });

  function sendPrompt() {
    const text = prompt.trim();
    if (!text || aiMutation.isPending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setPrompt("");
    aiMutation.mutate(text);
  }

  const publishMutation = useMutation({
    mutationFn: async () =>
      publish({
        data: {
          id: form.id,
          title: form.title,
          slug: form.slug,
          country: form.country,
          category: form.category,
          excerpt: form.excerpt,
          heroImageUrl: form.heroImageUrl,
          heroImageAlt: form.heroImageAlt,
          bodyHtml: form.bodyHtml,
          published: form.published,
        },
      }),
    onSuccess: (res) => {
      toast.success(`Live at /post/${res.article.slug}`);
      onPublished();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const autoAlt = generateImageAlt({
    title: form.title,
    country: form.country,
    category: form.category,
    url: form.heroImageUrl,
  });

  useEffect(() => {
    if (form.heroImageAltManual) return;
    if (form.heroImageAlt === autoAlt) return;
    setForm((prev) =>
      prev.heroImageAltManual ? prev : { ...prev, heroImageAlt: autoAlt },
    );
  }, [autoAlt, form.heroImageAlt, form.heroImageAltManual, setForm]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
      <section className="flex flex-col rounded-xl border border-primary/40 bg-card p-5">
        <h2 className="font-display text-xl font-bold text-foreground">AI Publishing Assistant</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Chat with Gemini. It detects the menu/category (creating new ones on request), formats the
          HTML, and can publish instantly.
        </p>
        {!hasGeminiKey ? (
          <p className="mt-3 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
            Save your Gemini API key in the “Gemini API key” tab first.
          </p>
        ) : null}

        <div className="mt-4 max-h-[420px] min-h-[240px] flex-1 space-y-3 overflow-y-auto rounded-lg border border-border bg-background p-3">
          {messages.map((m, i) => (
            <div
              key={i}
              className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
            >
              <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground"
                }`}
              >
                {m.text.length > 600 && m.role === "user"
                  ? `${m.text.slice(0, 600)}…`
                  : m.text}
                {m.link ? (
                  <a title="Open the original source article in a new tab"
                    className="mt-2 block font-semibold underline"
                    href={m.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View live post →
                  </a>
                ) : null}
              </div>
            </div>
          ))}
          {aiMutation.isPending ? (
            <p className="text-sm italic text-muted-foreground">Thinking and formatting…</p>
          ) : null}
          <div ref={chatEndRef} />
        </div>

        <textarea
          className={`${inputClass} mt-3 min-h-[120px] text-sm`}
          placeholder="Paste your article or type an instruction…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              sendPrompt();
            }
          }}
        />
        <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={autoPublish}
            onChange={(e) => setAutoPublish(e.target.checked)}
          />
          Publish automatically when the AI is done
        </label>
        <button
          className={`${btnPrimary} mt-3 w-full`}
          disabled={aiMutation.isPending || !prompt.trim()}
          onClick={sendPrompt}
        >
          {aiMutation.isPending ? "Working…" : "Send to AI"}
        </button>
      </section>


      <section className="rounded-xl border border-border bg-card p-5">
        <h2 className="font-display text-xl font-bold text-foreground">Article details</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Every field here is optional — the AI fills them in, and anything left blank is derived
          automatically. Only the body is required.
        </p>
        <div className="mt-4 space-y-4">
          <div>
            <label className={labelClass}>Title (H1) — optional</label>
            <input
              className={inputClass}
              placeholder="Auto-generated from the article if left blank"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Slug — optional</label>
              <input
                className={inputClass}
                placeholder="auto-from-title"
                value={form.slug}
                onChange={(e) => set({ slug: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClass}>Country category — optional</label>
              <select
                className={inputClass}
                value={form.country}
                onChange={(e) => set({ country: e.target.value })}
              >
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Menu / topic category — optional</label>
              <input
                className={inputClass}
                list="lww-categories"
                placeholder="Immigration, Visa, Digital Nomad…"
                value={form.category}
                onChange={(e) => set({ category: e.target.value })}
              />
              <datalist id="lww-categories">
                {[...new Set([...CATEGORIES, ...categories])].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Type a new name to create a new menu category.
              </p>
            </div>
            <div>
              <label className={labelClass}>Hero image URL — optional</label>
              <input
                className={inputClass}
                value={form.heroImageUrl}
                onChange={(e) => set({ heroImageUrl: e.target.value })}
              />
            </div>
          </div>
          {form.heroImageUrl.trim() ? (
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className={`${labelClass} mb-0`}>
                  Image alt text {form.heroImageAltManual ? "(custom)" : "(auto-generated)"}
                </label>
                <button
                  type="button"
                  className="text-[11px] font-semibold uppercase tracking-wide text-primary hover:underline"
                  onClick={() => set({ heroImageAlt: autoAlt, heroImageAltManual: false })}
                >
                  Reset to auto
                </button>
              </div>
              <input
                className={inputClass}
                value={form.heroImageAltManual ? form.heroImageAlt : autoAlt}
                onChange={(e) => set({ heroImageAlt: e.target.value, heroImageAltManual: true })}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Generated from the title, country and category keywords. Edit to override; body
                images missing alt text are filled automatically on publish.
              </p>
            </div>
          ) : null}
          <div>
            <label className={labelClass}>Meta description — optional</label>
            <textarea
              className={`${inputClass} min-h-[72px]`}
              value={form.excerpt}
              onChange={(e) => set({ excerpt: e.target.value })}
            />
          </div>

          <div>
            <label className={labelClass}>Body (HTML)</label>
            <textarea
              className={`${inputClass} min-h-[320px] font-mono text-xs`}
              value={form.bodyHtml}
              onChange={(e) => set({ bodyHtml: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set({ published: e.target.checked })}
            />
            Publish live immediately
          </label>
        </div>

        <button
          className={`${btnPrimary} mt-5 w-full`}
          disabled={publishMutation.isPending}
          onClick={() => publishMutation.mutate()}
        >
          {publishMutation.isPending ? "Publishing…" : "Publish Article"}
        </button>
      </section>
    </div>
  );
}

function ApiKeyPanel({
  hasGeminiKey,
  onSaved,
}: {
  hasGeminiKey: boolean;
  onSaved: () => void;
}) {
  const save = useServerFn(setGeminiKey);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setKey("");
  }, [hasGeminiKey]);

  return (
    <div className="max-w-xl rounded-xl border border-border bg-card p-5">
      <h2 className="font-display text-xl font-bold text-foreground">Gemini API key</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Stored server-side only. It is never sent to the browser — the dashboard only shows whether
        a key is saved.
      </p>
      <p className="mt-3 text-sm">
        Status:{" "}
        <span className="font-semibold text-foreground">
          {hasGeminiKey ? "Key saved" : "No key saved yet"}
        </span>
      </p>
      <input
        className={`${inputClass} mt-4`}
        type="password"
        placeholder="AIza…"
        value={key}
        onChange={(e) => setKey(e.target.value)}
      />
      <button
        className={`${btnPrimary} mt-4`}
        disabled={busy || !key.trim()}
        onClick={async () => {
          setBusy(true);
          try {
            await save({ data: { key } });
            toast.success("Gemini API key saved");
            setKey("");
            onSaved();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Saving…" : hasGeminiKey ? "Replace key" : "Save key"}
      </button>
    </div>
  );
}

/* -------------------- Community success stories moderation -------------------- */

function CommunityStoriesPanel() {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["admin-community-stories"],
    queryFn: useServerFn(adminListStories),
    retry: false,
  });
  const setStatus = useServerFn(adminSetStoryStatus);
  const removeStory = useServerFn(adminDeleteStory);
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-community-stories"] });

  if (list.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading submissions…</p>;
  }

  const rows = list.data ?? [];
  const visible = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const counts = {
    pending: rows.filter((r) => r.status === "pending").length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
    all: rows.length,
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {(["pending", "approved", "rejected", "all"] as const).map((key) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
              filter === key
                ? "bg-primary text-primary-foreground"
                : "border border-border text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            {key} ({counts[key]})
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nothing in this queue right now.
        </p>
      ) : (
        <ul className="mt-6 space-y-4">
          {visible.map((s) => (
            <li key={s.id} className="rounded-xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-secondary px-2.5 py-1 font-semibold">{s.country}</span>
                <span className="rounded-full bg-secondary px-2.5 py-1">{s.category}</span>
                <span className="rounded-full bg-secondary px-2.5 py-1">{s.rating}★</span>
                <span className="rounded-full bg-secondary px-2.5 py-1 capitalize">{s.status}</span>
                {s.report_count > 0 ? (
                  <span className="rounded-full bg-destructive/15 px-2.5 py-1 font-semibold text-destructive">
                    {s.report_count} report{s.report_count > 1 ? "s" : ""}
                  </span>
                ) : null}
                <span className="text-muted-foreground">
                  {new Date(s.created_at).toLocaleString("en-GB")}
                </span>
              </div>

              <p className="mt-3 text-sm font-semibold text-foreground">{s.display_name}</p>
              <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{s.story}</p>
              {s.moderation_note ? (
                <p className="mt-2 text-xs italic text-muted-foreground">AI note: {s.moderation_note}</p>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                {s.status !== "approved" ? (
                  <button
                    className={btnPrimary}
                    onClick={async () => {
                      await setStatus({ data: { id: s.id, status: "approved" } });
                      toast.success("Story approved and live");
                      await refresh();
                    }}
                  >
                    Approve
                  </button>
                ) : (
                  <button
                    className={btnGhost}
                    onClick={async () => {
                      await setStatus({ data: { id: s.id, status: "pending" } });
                      toast.success("Story unpublished");
                      await refresh();
                    }}
                  >
                    Unpublish
                  </button>
                )}
                {s.status !== "rejected" ? (
                  <button
                    className={btnGhost}
                    onClick={async () => {
                      await setStatus({ data: { id: s.id, status: "rejected" } });
                      toast.success("Story rejected");
                      await refresh();
                    }}
                  >
                    Reject
                  </button>
                ) : null}
                <button
                  className={`${btnGhost} text-destructive`}
                  onClick={async () => {
                    if (!confirm("Delete this story permanently?")) return;
                    await removeStory({ data: { id: s.id } });
                    toast.success("Story deleted");
                    await refresh();
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AiPostBox({
  categories,
  onChanged,
}: {
  categories: string[];
  onChanged: () => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [target, setTarget] = useState<string>("standalone");
  const [busy, setBusy] = useState(false);
  const runAi = useServerFn(aiAssistant);
  const publishToBox = useServerFn(adminGenerateBoxArticle);
  const saveBoxArticle = useServerFn(adminSaveBoxArticle);
  const data = useQuery({ queryKey: ["home-boxes-admin"], queryFn: useServerFn(adminHomeBoxes) });
  const boxes = data.data?.boxes ?? [];

  const mainNav = [
    { label: "Immigration", category: "Immigration" },
    { label: "Guides 2026", category: "Visa Guides 2026" },
    { label: "Jobs", category: "Jobs" },
    { label: "Remote Work", category: "Remote Work" },
    { label: "Business", category: "Business" },
    { label: "Health", category: "Healthcare" },
    { label: "Wealth", category: "Cross-Border Wealth" },
    { label: "Finance", category: "Finance" },
    { label: "Credit Cards", category: "Credit Cards" },
    { label: "Living Cost", category: "Living Cost" },
    { label: "Investment", category: "Investment" },
    { label: "Tax", category: "Tax" },
    { label: "Success Stories", category: "Success Stories" },
  ];

  async function handleGenerate() {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      if (target === "standalone" || target.startsWith("nav:")) {
        const categoryOverride = target.startsWith("nav:") ? target.split(":")[1] : undefined;
        // The AI assistant automatically detects country and category. 
        // We pass the prompt + instruction if it's a specific nav category.
        const finalPrompt = categoryOverride 
          ? `[Target Category: ${categoryOverride}]\n\n${prompt}`
          : prompt;
          
        const res = await runAi({ data: { prompt: finalPrompt, autoPublish: true } });
        toast.success(res.message);
      } else if (target.startsWith("country:")) {
        const countryCode = target.split(":")[1];
        const finalPrompt = `[Target Country: ${countryCode.toUpperCase()}]\n\n${prompt}`;
        const res = await runAi({ data: { prompt: finalPrompt, autoPublish: true } });
        toast.success(res.message);
      } else {
        toast.info("Generating visuals and publishing...");
        const res = await publishToBox({ data: { boxId: target, prompt } });
        await saveBoxArticle({
          data: {
            boxId: target,
            title: res.title,
            slug: res.slug,
            excerpt: res.excerpt,
            heroImageUrl: res.heroImageUrl,
            heroImageAlt: res.heroImageAlt,
            bodyHtml: res.bodyHtml,
            published: true,
          }
        });
        toast.success(`Published to "${boxes.find((b: any) => b.id === target)?.title}"`);
      }
      setPrompt("");
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Generation failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 rounded-xl border border-border bg-card p-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-foreground">AI POST BOX</h2>
        <p className="text-sm text-muted-foreground">
          Enter a prompt or article. The AI will format it (Gold H1, Dark-mode H2/H3), strip meta labels, 
          generate visuals/charts, and publish it instantly to the selected destination.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className={labelClass}>Target Location</label>
          <select 
            className={inputClass} 
            value={target} 
            onChange={(e) => setTarget(e.target.value)}
          >
            <option value="standalone">Standalone Pillar (Auto-detect Category)</option>
            
            <optgroup label="Main Navigation Menu">
              {mainNav.map((n) => (
                <option key={n.category} value={`nav:${n.category}`}>{n.label}</option>
              ))}
            </optgroup>

            <optgroup label="Country Specific Hubs">
              {COUNTRIES.map((c) => (
                <option key={c} value={`country:${c}`}>{c.toUpperCase()} Hub</option>
              ))}
            </optgroup>

            <optgroup label="Homepage Pillars (Pillar Boxes)">
              {boxes.map((b: any) => (
                <option key={b.id} value={b.id}>{b.title}</option>
              ))}
            </optgroup>
          </select>
        </div>

        <div>
          <label className={labelClass}>Content / Prompt</label>
          <textarea
            className={`${inputClass} min-h-[400px] font-mono text-sm`}
            placeholder="Paste your article content or a detailed prompt here..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        <button 
          className={`${btnPrimary} w-full h-12 text-lg shadow-[0_0_20px_rgba(212,175,55,0.2)]`}
          disabled={busy || !prompt.trim()}
          onClick={handleGenerate}
        >
          {busy ? "Processing & Publishing..." : "Generate & Publish Instantly"}
        </button>
      </div>

      {prompt && (
        <div className="mt-8 border-t border-border pt-6">
          <label className={labelClass}>Live Structure Preview</label>
          <div className="rounded-lg border border-border bg-background/50 p-4">
            <ArticlePreview source={prompt} />
          </div>
        </div>
      )}
    </div>
  );
}



